import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToCleaningGuestReservations } from "../../../../redux/cleaningReservationSlice";
import { listenToMultiCleanings } from "../../../../redux/cleaningSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { motion } from "framer-motion";
import { Call } from "@mui/icons-material";

/* ── green tokens ── */
const G = {
  green:      "#16a34a",
  greenDark:  "#14532d",
  greenLight: "#dcfce7",
  border:     "#bbf7d0",
  muted:      "#6b7280",
  text:       "#14532d",
  surface:    "#ffffff",
  bg:         "#f0fdf4",
  sans:       "'Montserrat', sans-serif",
  serif:      "'Cormorant Garamond', serif",
};

const CleaningReviewPreview: React.FC = () => {
  const guest   = useSelector((s: RootState) => s.auth.user);
  const guestId = guest?.userId;

  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const reservations = useSelector((s: RootState) => s.cleaningReservations.guestReservations);
  const listings     = useSelector((s: RootState) => s.cleaning.listings);
  const allUsers     = useSelector((s: RootState) => s.auth.allUsers);

  const [fetchedIds, setFetchedIds] = useState<string[]>([]);

  useEffect(() => {
    if (guestId) {
      dispatch(listenToCleaningGuestReservations(guestId));
      dispatch(fetchAllUsers());
    }
  }, [dispatch, guestId]);

  useEffect(() => {
    reservations.forEach((r) => {
      if (!fetchedIds.includes(r.listingId)) {
        dispatch(listenToMultiCleanings([r.listingId]));
        setFetchedIds((prev) => [...prev, r.listingId]);
      }
    });
  }, [reservations, fetchedIds, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    listings.forEach((l) => (m[l.listingId] = l));
    return m;
  }, [listings]);

  /* only show reservations that are checking out or completed */
  const filteredReservations = useMemo(
    () => reservations.filter((r) => (r.isCheckingOut || r.isCompleted) && !r.isCancelled),
    [reservations]
  );

  const findHost = (hostId: string) => allUsers.find((u) => u.userId === hostId) || null;

  return (
    <div style={s.wrap}>
      {filteredReservations.length === 0 ? (
        <div style={s.empty}>
          <span style={{ fontSize: 48 }}>🧹</span>
          <p style={s.emptyTitle}>No services ready for review yet.</p>
          <p style={s.emptyText}>Once your cleaning service ends, you can leave a review here.</p>
        </div>
      ) : (
        filteredReservations.map((res, idx) => {
          const listing     = listingsMap[res.listingId];
          const hostProfile = findHost(res.hostId) || {};
          const hostInitial = (hostProfile as any).firstName?.[0]?.toUpperCase() || "C";
          const hostImg     = (hostProfile as any).profilePicture;

          const checkIn  = res.checkIn  ? new Date(res.checkIn).toLocaleDateString("en-GB",  { day: "numeric", month: "short", year: "numeric" }) : "—";
          const checkOut = res.checkOut ? new Date(res.checkOut).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

          return (
            <motion.div
              key={res.id}
              style={s.card}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
            >
              {/* ── listing header ── */}
              <div style={s.cardTop}>
                <div style={s.imgWrap}>
                  {listing?.photos?.[0]
                    ? <img src={listing.photos[0]} alt="Listing" style={s.listingImg} />
                    : <div style={s.imgPlaceholder}>🧹</div>
                  }
                </div>
                <div style={s.cardText}>
                  <h3 style={s.title}>{listing?.name || listing?.title || "Cleaning Service"}</h3>
                  <p style={s.date}>{checkIn} → {checkOut}</p>
                  <div style={s.badge}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: G.green }} />
                    <span>Service Completed</span>
                  </div>
                </div>
              </div>

              {/* ── review button ── */}
              <button
                onClick={() => navigate(`/cleaningrating/${guestId}/${res.listingId}/${res.id}`)}
                style={s.reviewBtn}
              >
                ✦ Review This Service
              </button>

              {/* ── host card ── */}
              <div style={s.hostCard}>
                {hostImg ? (
                  <img
                    src={hostImg}
                    alt="Host"
                    style={s.hostAvatar}
                    onClick={() => navigate(`/aboutme/${res.hostId}`)}
                  />
                ) : (
                  <div style={s.hostInitial} onClick={() => navigate(`/aboutme/${res.hostId}`)}>
                    {hostInitial}
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <p style={s.hostName}>
                    {(hostProfile as any).firstName || "Cleaner"} {(hostProfile as any).lastName || ""}
                  </p>
                  <p style={s.hostLabel}>Your Cleaner</p>
                </div>

                <div style={s.actions}>
                  <button style={s.messageBtn} onClick={() => navigate(`/imessages/${res.hostId}`)}>
                    💬 Message
                  </button>
                  {(hostProfile as any).phoneNumber && (
                    <button style={s.callBtn} onClick={() => window.open(`tel:${(hostProfile as any).phoneNumber}`)}>
                      <Call style={{ fontSize: 18, color: G.green }} />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })
      )}
    </div>
  );
};

export default CleaningReviewPreview;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  wrap:       { display: "flex", flexDirection: "column", gap: 20, padding: "20px 16px", maxWidth: 600, margin: "0 auto" },
  empty:      { textAlign: "center", padding: "80px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: G.text },
  emptyText:  { fontSize: 14, color: G.muted },

  card: {
    background: G.surface,
    border: `1px solid ${G.border}`,
    borderRadius: 18,
    boxShadow: "0 8px 24px rgba(22,163,74,.08)",
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  cardTop:       { display: "flex", gap: 14, alignItems: "flex-start" },
  imgWrap:       { flexShrink: 0 },
  listingImg:    { width: 90, height: 90, borderRadius: 12, objectFit: "cover", border: `2px solid ${G.border}` },
  imgPlaceholder:{ width: 90, height: 90, borderRadius: 12, background: G.greenLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 },
  cardText:      { flex: 1, display: "flex", flexDirection: "column", gap: 4 },
  title:         { fontSize: 15, fontWeight: 700, color: G.text, margin: 0 },
  date:          { fontSize: 13, color: G.muted, margin: 0 },
  badge:         { display: "inline-flex", alignItems: "center", gap: 5, background: G.greenLight, border: `1px solid ${G.border}`, borderRadius: 999, padding: "3px 9px", fontSize: 11, fontWeight: 600, color: G.text, width: "fit-content" },

  reviewBtn: {
    width: "100%", padding: "12px 0",
    background: `linear-gradient(135deg, ${G.green} 0%, ${G.greenDark} 100%)`,
    color: "#fff", border: "none", borderRadius: 12,
    fontWeight: 700, fontSize: 14, cursor: "pointer",
    letterSpacing: "0.02em",
    boxShadow: "0 4px 14px rgba(22,163,74,.3)",
  },

  hostCard:     { background: G.bg, borderRadius: 12, padding: "10px 14px", border: `1px solid ${G.border}`, display: "flex", alignItems: "center", gap: 12 },
  hostAvatar:   { width: 46, height: 46, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: `2px solid ${G.green}`, flexShrink: 0 },
  hostInitial:  { width: 46, height: 46, borderRadius: "50%", background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16, cursor: "pointer", flexShrink: 0 },
  hostName:     { fontSize: 14, fontWeight: 600, color: G.text, margin: 0 },
  hostLabel:    { fontSize: 11, color: G.muted, margin: 0 },
  actions:      { display: "flex", alignItems: "center", gap: 8 },
  messageBtn:   { background: G.greenDark, color: "#fff", border: "none", borderRadius: 8, padding: "7px 12px", fontWeight: 600, fontSize: 12, cursor: "pointer" },
  callBtn:      { background: "transparent", border: `1px solid ${G.green}`, borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
};
